import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../components/common/PageLayout';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;

  return (
    <>
      <Helmet>
        <title>Terms of Service | MWQQ.com - Passport Photo Tool</title>
        <meta name="description" content="Terms of service for MWQQ.com passport photo tool. Understand your rights and our service terms. Privacy-first, browser-based photo processing." />
        <meta name="keywords" content="terms of service, user agreement, passport photo terms, service conditions, legal terms" />
        <link rel="canonical" href={`${baseUrl}/terms.html`} />
        <meta property="og:title" content="Terms of Service | MWQQ.com" />
        <meta property="og:description" content="Terms of service for our privacy-first passport photo tool." />
        <meta property="og:url" content={`${baseUrl}/terms.html`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageLayout title={t('pages.terms.title')}>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('pages.terms.lastUpdated')}: October 1, 2025
      </p>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.acceptance.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.acceptance.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.serviceDescription.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.serviceDescription.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.serviceDescription.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.userResponsibilities.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.userResponsibilities.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.userResponsibilities.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.disclaimer.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.disclaimer.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.disclaimer.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.limitations.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.limitations.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.limitations.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.intellectualProperty.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.intellectualProperty.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.intellectualProperty.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.privacy.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.privacy.content')}{' '}
          <Link to="/privacy.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.termination.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.termination.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.termination.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.governing.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.terms.sections.governing.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.terms.sections.contact.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.terms.sections.contact.content')}
        </p>
        <a
          href="mailto:mwqq.com@gmail.com"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          mwqq.com@gmail.com
        </a>
      </section>

      <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Related:{' '}
          <Link to="/privacy.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.privacy')}
          </Link>
          {' | '}
          <Link to="/cookies.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.cookies')}
          </Link>
        </p>
      </div>
      </PageLayout>
    </>
  );
};

export default TermsOfService;
